import {eppiCostumeApi, type EppiCostumeListResult} from '../api/eppiCostumeApi';
import {
    BubbleStyleSpec,
    EppiCostume,
    EppiCostumeDetail,
    FALLBACK_BUBBLE_STYLES,
    FinalizeCostumeRequest,
    FinalizeCostumeResponse,
    GenerateCostumeRequest,
    GenerateCostumeResponse,
    PatchCostumeRequest,
    RequestHistoryItem,
} from '../types/eppiCostume';
import {
    useBaseMutation,
    useBaseQuery,
    useInvalidateQueries,
} from './useBaseQuery';

// ── Query Key Factory ────────────────────────────────────────────────────────

export const eppiCostumeKeys = {
    all: ['eppiCostumes'] as const,
    lists: () => [...eppiCostumeKeys.all, 'list'] as const,
    list: () => [...eppiCostumeKeys.lists()] as const,
    details: () => [...eppiCostumeKeys.all, 'detail'] as const,
    detail: (code: string) => [...eppiCostumeKeys.details(), code] as const,
    histories: () => [...eppiCostumeKeys.all, 'history'] as const,
    history: (code: string) => [...eppiCostumeKeys.histories(), code] as const,
    bubbleStyles: () => [...eppiCostumeKeys.all, 'bubbleStyles'] as const,
};

const ACTIVE_HISTORY_STATUSES = new Set([
    'PENDING',
    'RUNNING',
    'AWAITING_RESULTS',
]);

// ── Queries ──────────────────────────────────────────────────────────────────

export const useEppiCostumes = () => {
    return useBaseQuery<EppiCostumeListResult>(eppiCostumeKeys.list(), () =>
        eppiCostumeApi.list()
    );
};

export const useEppiCostumeDetail = (code: string | null | undefined) => {
    return useBaseQuery<EppiCostumeDetail>(
        eppiCostumeKeys.detail(code ?? ''),
        () => eppiCostumeApi.getByCode(code as string),
        {enabled: !!code}
    );
};

export const useEppiCostumeHistory = (code: string | null | undefined) => {
    return useBaseQuery<RequestHistoryItem[]>(
        eppiCostumeKeys.history(code ?? ''),
        () => eppiCostumeApi.listRequests(code as string),
        {
            enabled: !!code,
            // 진행 중 request 1개라도 있으면 5초 polling, 모두 종료면 중단
            refetchInterval: (query) => {
                const data = query.state.data as
                    | RequestHistoryItem[]
                    | undefined;
                if (!data || data.length === 0) return false;
                const hasActive = data.some((r) =>
                    ACTIVE_HISTORY_STATUSES.has(r.status)
                );
                return hasActive ? 5000 : false;
            },
        }
    );
};

/**
 * Painter 카탈로그 (V200). backend `GET /admin/bubble-styles` 가 미배포 상태거나
 * 빈 응답을 주면 FALLBACK_BUBBLE_STYLES 로 graceful degrade.
 */
export const useBubbleStyleCatalog = () => {
    return useBaseQuery<BubbleStyleSpec[]>(
        eppiCostumeKeys.bubbleStyles(),
        async () => {
            try {
                const list = await eppiCostumeApi.listBubbleStyles();
                return list.length > 0 ? list : FALLBACK_BUBBLE_STYLES;
            } catch {
                // backend 트랙 미머지 등 — 정적 카탈로그로 폼 동작 보장
                return FALLBACK_BUBBLE_STYLES;
            }
        },
        // 카탈로그는 자주 바뀌지 않음 — 폼 진입마다 refetch 불필요
        {staleTime: 5 * 60 * 1000}
    );
};

// ── Mutations ────────────────────────────────────────────────────────────────

export const useGenerateEppiCostume = () => {
    const {invalidateMultiple} = useInvalidateQueries();
    return useBaseMutation<GenerateCostumeRequest, GenerateCostumeResponse>(
        (body) => eppiCostumeApi.generate(body),
        {
            onSuccess: (_data, vars) => {
                // 새 request가 이력 최상단에 자동 노출되도록 history 무효화
                invalidateMultiple([
                    eppiCostumeKeys.history(vars.code),
                    eppiCostumeKeys.detail(vars.code),
                ]);
            },
        }
    );
};

export interface FinalizeVars {
    code: string;
    body: FinalizeCostumeRequest;
}

export const useFinalizeEppiCostume = () => {
    const {invalidateMultiple} = useInvalidateQueries();
    return useBaseMutation<FinalizeVars, FinalizeCostumeResponse>(
        ({code, body}) => eppiCostumeApi.finalize(code, body),
        {
            onSuccess: (_data, vars) => {
                invalidateMultiple([
                    eppiCostumeKeys.list(),
                    eppiCostumeKeys.detail(vars.code),
                    eppiCostumeKeys.history(vars.code),
                ]);
            },
        }
    );
};

export interface PatchCostumeVars {
    code: string;
    body: PatchCostumeRequest;
}

export const usePatchEppiCostume = () => {
    const {invalidateMultiple} = useInvalidateQueries();
    return useBaseMutation<PatchCostumeVars, EppiCostume>(
        ({code, body}) => eppiCostumeApi.patch(code, body),
        {
            onSuccess: (_data, vars) => {
                invalidateMultiple([
                    eppiCostumeKeys.list(),
                    eppiCostumeKeys.detail(vars.code),
                ]);
            },
        }
    );
};
